import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { DE$ } from '@infl-ts/util-datum';
import { useToggle } from '@infl-ts/util-hooks';
import { makeUnknownError } from '@infl-ts/util-query-errors';
import { getParam } from '@infl-ts/util-url';
import * as DE from '@nll/datum/lib/DatumEither';
import * as E from 'fp-ts/Either';
import { constNull, constVoid, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as t from 'io-ts';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePublicResponseOrComment } from './queries';
var ScrollToParam = t.type({
  scrollTo: t.string
});

var getStageId = function getStageId(result) {
  return pipe(result, DE$.chainW(function (_ref) {
    var node = _ref.node;
    return node === null ? DE.failure(makeUnknownError({
      name: 'NoIdMatch',
      message: 'Could not find matching id'
    })) : DE.success(node.__typename === 'PublicResponse' ? node.stage.id : node.publicResponse.stage.id);
  }), DE$.getSuccessOrElse(null));
};

var getResponseId = function getResponseId(item) {
  return pipe(item, O.fromNullable, O.map(function (item) {
    return item.__typename === 'PublicResponse' ? item.id : item.publicResponse.id;
  }), O.getOrElseW(constNull));
};

var useGetFocusedItemFromUrl = function useGetFocusedItemFromUrl() {
  var location = useLocation();

  var _useState = useState(null),
      focusedId = _useState[0],
      setFocusedId = _useState[1];

  var _useState2 = useState(null),
      focusedItem = _useState2[0],
      setFocusedItem = _useState2[1];

  var _usePublicResponseOrC = usePublicResponseOrComment({
    variables: {
      id: focusedId !== null && focusedId !== void 0 ? focusedId : ''
    },
    skip: focusedId === null,
    onCompleted: E.match(console.error, function (_ref2) {
      var node = _ref2.node;
      return node !== null && setFocusedItem(node);
    })
  }),
      result = _usePublicResponseOrC.result,
      fetchMore = _usePublicResponseOrC.fetchMore;

  useEffect(function () {
    return pipe(getParam('cp', location), function (cpParams) {
      return ScrollToParam.is(cpParams) ? O.some(cpParams.scrollTo) : O.none;
    }, O.match(constVoid, function (id) {
      setFocusedId(id);
      void fetchMore({
        variables: {
          id: id
        }
      });
    }));
  }, [location]);
  return {
    clearFocused: function clearFocused() {
      setFocusedId(null);
      setFocusedItem(null);
    },
    focusedItem: focusedItem,
    focusedStageId: getStageId(result)
  };
};

var handleScrollToResponse = function handleScrollToResponse(focusedElement, highlight, unhighlight, clearFocused) {
  focusedElement.scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  });
  highlight();
  var timer = setTimeout(function () {
    unhighlight();
    clearFocused();
  }, 2000);
  return function () {
    return clearTimeout(timer);
  };
};

var useFocusOnResponse = function useFocusOnResponse() {
  var _useGetFocusedItemFro = useGetFocusedItemFromUrl(),
      clearFocused = _useGetFocusedItemFro.clearFocused,
      focusedItem = _useGetFocusedItemFro.focusedItem,
      focusedStageId = _useGetFocusedItemFro.focusedStageId;

  var useScrollToFocused = function useScrollToFocused() {
    var focusedRef = useRef(null);

    var _useToggle = useToggle(false),
        _useToggle2 = _slicedToArray(_useToggle, 2),
        shouldHighlight = _useToggle2[0],
        _useToggle2$ = _useToggle2[1],
        highlight = _useToggle2$.on,
        unhighlight = _useToggle2$.off;

    useEffect(function () {
      if (focusedRef.current !== null) return handleScrollToResponse(focusedRef.current, highlight, unhighlight, clearFocused);
    }, [highlight, unhighlight]);
    return {
      shouldHighlight: shouldHighlight,
      focusedRef: focusedRef,
      focusedResponseId: getResponseId(focusedItem),
      focusedCommentId: (focusedItem === null || focusedItem === void 0 ? void 0 : focusedItem.__typename) === 'PublicResponseComment' ? focusedItem.id : null
    };
  };

  return {
    focusedStageId: focusedStageId,
    useScrollToFocused: useScrollToFocused
  };
};

export default useFocusOnResponse;